import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from './PageWrapper';
import Page from './Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import './GroupsHome.scss';
import { Alert, Anchor, Dropdown, DropdownItem, Popup, Tooltip } from '@spark-labs/avocado-ui';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleButton,
  BubbleInput,
  BubbleDebounce,
  BubbleOverlay,
  BubbleTable,
  BubbleDrawer,
  BubbleAccordion,
  BubbleFormGroup,
  BubbleBadge,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { BubbleTableFields, BubbleTableTemplate } from '@go-client/components/BubbleUI/BubbleTable';
import {
  bare,
  bareVenueRequest,
  exportGroupBookings,
  exportGroupBookingsBrand,
  exportGroupMemo,
  exportGroupReportCSV,
  update as updateGroupBooking,
} from '@go-client/lib/api/groupBooking';
import {
  groupBy,
  GroupedItems,
  intersection,
  mapBy,
  notUndefined,
  onlyUnique,
} from '@go-shared/lib/functional';
import {
  GroupBooking,
  GroupSubgroup,
  VenueRequest,
  VenueRequestStatus,
  venueRequestStatuses,
} from '@go-shared/types/groupBooking';
import { hashStringToColor, makeFullName } from '@go-shared/lib/utils';
import {
  timeToString,
  isoDate,
  today,
  addDaysToUTCDate,
  calculateTimeOffset,
  getLocaleUTCDateString,
  timeCompare,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';
import GroupBookingForm from '@go-client/components/GroupBookingForm/GroupBookingForm';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import GroupsGlance from '@go-client/components/GroupsGlance';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import Fuse from 'fuse.js';
import { findSailingBundleByDateAndDay, getDayLocation } from '@go-shared/lib/sailing';
import { ObjectId } from 'bson';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  bulkBookingStatusChange,
  fetchDataBundle,
  fetchMultiDataBundle,
  GroupBookingDataBundle,
} from '@go-client/components/GroupBookingForm/Workflows';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import uuid from 'uuid';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import {
  canApproveOrRejectABooking,
  canBulkSelectBookings,
  canChangeBookingStatus,
  canCreateOrEditBookings,
  canExportGroupBookingAllProperties,
  canViewGroupsMultiPropertyCheckbox,
} from '@go-shared/lib/permissions';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { Property } from '@go-shared/types/property';
import { getHourFormat } from '@go-shared/lib/property';
import { DayMarkersButton } from '@go-client/components/DayMarkersButton';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import { groupBookingClient } from '@go-client/lib/api/v2/groupBooking';
import { safeFetch } from '@go-client/lib/api';
import { getUserToken } from '@go-client/lib/auth';

export interface DefaultMode {
  type: 'default';
}

interface GroupEditMode {
  type: 'group-edit';
  groupId?: string;
}

interface SubgroupEditMode {
  type: 'subgroup-edit';
  groupId: string;
  subgroupId: string;
}

export interface BookingEditMode {
  type: 'edit';
  groupId: string;
  bookingId: string;
}

export interface BookingCreateMode {
  type: 'create';
  groupId?: string;
  bookingId?: string;
  startAtPage: number;
}

interface GroupsTableItem {
  id: string;
  name: string;
  property: string;
  venue: string;
  voyage: string;
  location: string;
  color: string;
  day: number;
  date: string;
  time: string;
  requester: string;
  status: VenueRequestStatus;
  foodBeverage: string[];
  entertainment: string[];
  housekeeping: string;
  isFirstReq: boolean;
  isLastReq: boolean;
  isExpandedReq: boolean;
  totalNumRows: number;
  amtSkippedReqs: number;
  hasExpandedReq: boolean;
  checked: boolean;
  _group: GroupBooking;
  _booking: VenueRequest;
  _sailingDay?: BareSimpleSailingDay;
}

type GroupsTableFields = BubbleTableFields<GroupsTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

interface GroupsFilters {
  startDate?: string;
  endDate?: string;
  selectedVoyages: Array<BubbleTypeaheadValue<string>>;
  selectedLocations: Array<BubbleTypeaheadValue<string>>;
  selectedStatuses: Array<BubbleTypeaheadValue<string>>;
  selectedVenues: Array<BubbleTypeaheadValue<VenueTemplate>>;
  selectedProperties: Array<BubbleTypeaheadValue<Property>>;
  groupId: string;
  showAllOfGroupId: string;
  groupByGroup: boolean;
  multiPropertyMode: boolean;
  visibleFields: { [key: string]: boolean };
}

export type GroupsMode =
  | DefaultMode
  | SubgroupEditMode
  | GroupEditMode
  | BookingEditMode
  | BookingCreateMode;

interface GroupsHomeParams {
  property: string;
}

interface CreateSubgroupForm {
  letter: string;
  number: string;
  _advanced: boolean;
}

function generateDefaultSubgroupForm(subgroups: GroupSubgroup[]): CreateSubgroupForm {
  let letr = '';
  let numb = '';

  if (subgroups) {
    const usedOpts = subgroups.map(sg => sg.label);

    const allOpts = Array.from(Array(26))
      .map((_, i) => i + 65)
      .map(l => Array.from(Array(10).keys()).map(n => `${String.fromCharCode(l)}${n}`))
      .flat();

    const firstAvailOpt = allOpts.find(opt => !usedOpts.includes(opt)) || allOpts[0];

    letr = firstAvailOpt[0];
    numb = firstAvailOpt[1];
  }

  return {
    letter: letr,
    number: numb,
    _advanced: false,
  };
}

type GroupsHomeDataBundle = GroupBookingDataBundle & {
  sailingDayMap: GroupedItems<{
    dateIso: string;
    dayIndex: number;
    sailing: BareSimpleSailing;
    day: BareSimpleSailingDay;
  }>;
};

type GroupsHomePageProps = RouteComponentProps<GroupsHomeParams>;

const GroupsHome: React.FC<GroupsHomePageProps> = (props: GroupsHomePageProps) => {
  const serverInfo = useServerInfo();
  const { activeProperty } = useActiveProperty();
  const { myProperties } = usePropertyInfo();
  const { user } = useUserInfo();

  const [dataBundle, setDataBundle] = useState<GroupsHomeDataBundle>({
    groupBookings: [],
    simpleGroupBookings: [],
    simpleSailings: [],
    venueTemplates: [],
    allUsers: [],
    sailingDayMap: {},
  });
  const mixedDataBundle = useMemo(() => {
    const simpleSailings = dataBundle.simpleSailings.concat(
      dataBundle.auxData?.simpleSailings ?? []
    );

    const sailingDayMap = groupBy(
      simpleSailings
        .map(s =>
          s.days.map((d, i) => ({ dateIso: isoDate(d.date), dayIndex: i, sailing: s, day: d }))
        )
        .flat(),
      'dateIso'
    );

    return {
      groupBookings: dataBundle.groupBookings.concat(dataBundle.auxData?.groupBookings ?? []),
      simpleGroupBookings: dataBundle.simpleGroupBookings.concat(
        dataBundle.auxData?.simpleGroupBookings ?? []
      ),
      simpleSailings,
      venueTemplates: dataBundle.venueTemplates.concat(dataBundle.auxData?.venueTemplates ?? []),
      allUsers: dataBundle.allUsers.concat(dataBundle.auxData?.allUsers ?? []),
      sailingDayMap,
    };
  }, [dataBundle]);

  const propertyOpts = myProperties
    .filter(s => s.active)
    .map(s => ({
      key: s._id,
      item: s,
    }));

  const voyageOpts = mixedDataBundle.simpleSailings
    .map(s => s.name)
    .filter(onlyUnique)
    .map(n => ({
      key: n,
      item: n,
    }));

  let allLocations: string[] = [];
  mixedDataBundle.simpleSailings.forEach(sail => {
    sail.days.forEach(day =>
      getDayLocation(day)
        .split('/')
        .forEach(n => allLocations.push(n))
    );
  });
  allLocations = allLocations.filter(onlyUnique);
  const locationOpts = allLocations.map(p => ({
    key: p,
    item: p,
  }));

  const venueOpts = mixedDataBundle.venueTemplates.map(n => ({
    key: n._id,
    item: n,
  }));
  const venueOptsMap = mapBy(venueOpts, 'key');

  const statusOpts = venueRequestStatuses.map(s => ({
    key: s,
    item: s,
    variant: makeVariantFromStatus(s),
  }));
  const statusOptsMap = mapBy(statusOpts, 'key');

  const DEFAULT_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => !['cancelled', 'draft'].includes(opt.item)),
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: true,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: true,
      location: true,
      day: true,
      venue: true,
      date: true,
      time: true,
      requester: false,
      status: true,
      foodBeverage: false,
      entertainment: false,
      housekeeping: false,
      markers: true,
      actions: true,
    },
    multiPropertyMode: false,
  };

  const FOOD_BEVERAGE_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => ['approved'].includes(opt.item)),
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: false,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: false,
      location: false,
      day: false,
      venue: true,
      date: true,
      time: true,
      requester: false,
      status: false,
      foodBeverage: true,
      markers: true,
      actions: true,
    },
  };

  const ENTERTAINMENT_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => ['approved'].includes(opt.item)),
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: false,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: false,
      location: false,
      day: false,
      venue: true,
      date: true,
      time: true,
      requester: false,
      status: false,
      entertainment: true,
      markers: true,
      actions: true,
    },
  };

  const HOUSEKEEPING_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => ['approved'].includes(opt.item)),
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: false,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: false,
      location: false,
      day: false,
      venue: true,
      date: true,
      time: true,
      requester: false,
      status: false,
      housekeeping: true,
      markers: true,
      actions: true,
    },
  };

  const hourFormat = getHourFormat(activeProperty);
  const [isBusy, setIsBusy] = useState({ exportReport: false, exportBooking: false });
  const [expandedReqId, setExpandedReqId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<GroupsFilters>({
    startDate: today(),
    endDate: isoDate(addDaysToUTCDate(new Date(today()), 180)),
    selectedVoyages: [],
    selectedLocations: [],
    selectedVenues: [],
    selectedProperties: [],
    ...DEFAULT_FILTERS,
  });
  const [selectedTableItems, setSelectedTableItems] = useState<GroupsTableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<GroupsMode>({ type: 'default' });
  const [createSubgroupForm, setCreateSubgroupForm] = useState<CreateSubgroupForm | undefined>(
    undefined
  );
  const [firstFiltersSet, setFirstFiltersSet] = useState(false);
  const [filteredItems, setFilteredItems] = useState<GroupsTableItem[]>([]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [activeProperty, filters.selectedProperties, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (filters.multiPropertyMode && filters.selectedProperties.length === 0) {
      // user is activating multi property mode, do initializations required
      setFilters({
        ...filters,
        selectedProperties: propertyOpts.map(p => ({
          ...p,
          key: uuid(),
          refKey: p.key,
        })),
      });
    } else if (!filters.multiPropertyMode && filters.selectedProperties.length > 0) {
      // user is deactivating multi property mode, do cleanups required
      setFilters({
        ...filters,
        selectedProperties: [],
      });
    }
  }, [filters.multiPropertyMode]);

  useEffect(() => {
    if (mixedDataBundle.venueTemplates.length && !firstFiltersSet) {
      setFilters({
        ...filters,
        selectedVenues: venueOpts
          .map(vopt => {
            const refOpt = venueOptsMap[vopt.key];
            return refOpt && (user.groupApprovingVenues || []).includes(vopt.item._id)
              ? {
                  key: uuid(),
                  item: vopt.item,
                  refKey: refOpt.key,
                }
              : undefined;
          })
          .filter(notUndefined),
      });
      setFirstFiltersSet(true);
    }
  }, [mixedDataBundle.venueTemplates]);

  async function fetchData() {
    setLoading(true);
    setSelectedTableItems([]);

    try {
      const data = await fetchDataBundle(activeProperty, filters.startDate, filters.endDate);
      const auxData = filters.multiPropertyMode
        ? await fetchMultiDataBundle(
            filters.selectedProperties.map(p => p.item).filter(p => p._id !== activeProperty._id)
          )
        : undefined;
      const sailingDayMap = groupBy(
        data.simpleSailings
          .map(s =>
            s.days.map((d, i) => ({ dateIso: isoDate(d.date), dayIndex: i, sailing: s, day: d }))
          )
          .flat(),
        'dateIso'
      );

      setDataBundle({ ...data, sailingDayMap, auxData });
      return data;
    } finally {
      setLoading(false);
    }
  }

  const fuse = useMemo(() => {
    return new Fuse(
      mixedDataBundle.groupBookings
        .map(gbk =>
          gbk.requests.map(req => ({ _id: req._id, name: req.name, groupName: gbk.party.name }))
        )
        .flat(),
      {
        ...DEFAULT_FUSE_SETTINGS,
        keys: ['name', 'groupName'],
      }
    );
  }, [mixedDataBundle.groupBookings]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const tableItems: GroupsTableItem[] = useMemo(() => {
    const bookingsSortedByGroups = mixedDataBundle.groupBookings
      .filter(gbk => {
        // first filter: only specific groups (by group id)
        const meetsGroupIdSearch = !filters.groupId
          ? true
          : (gbk.party.groupId || '').startsWith(filters.groupId);
        return meetsGroupIdSearch;
      })
      .map(gbk => {
        const relevantReqs = gbk.requests
          // calculate additional filters and add as metadata
          .map(req => {
            const sailingDayInfo = (mixedDataBundle.sailingDayMap[isoDate(req.date)] || []).find(
              m => m.dayIndex === req.dayIndex
            );

            const meetsStartDate = isoDate(req.date) >= (filters.startDate || isoDate(req.date));
            const meetsEndDate = isoDate(req.date) <= (filters.endDate || isoDate(req.date));
            const meetsNameSearch =
              !fuseSearchResults || fuseSearchResults.findIndex(sr => sr._id === req._id) > -1;
            const meetsVoyageSearch =
              filters.selectedVoyages.length < 1 ||
              filters.selectedVoyages
                .map(sv => sv.item)
                .includes(sailingDayInfo?.sailing.name || '');
            const meetsLocationSearch =
              filters.selectedLocations.length < 1 ||
              intersection(
                (sailingDayInfo?.day.locations ?? []).map(s => s.name.trim().toLowerCase()),
                filters.selectedLocations.map(c => c.item.trim().toLowerCase())
              ).length > 0;
            const meetsVenueSearch =
              filters.selectedVenues.length < 1 ||
              filters.selectedVenues.map(sv => sv.item._id).includes(req.venue._id);
            const meetsStatusSearch =
              filters.selectedStatuses.length < 1 ||
              filters.selectedStatuses.map(sv => sv.item).includes(req.status);

            return {
              ...req,
              _meetsFilters:
                meetsStartDate &&
                meetsEndDate &&
                meetsNameSearch &&
                meetsVoyageSearch &&
                meetsLocationSearch &&
                meetsVenueSearch &&
                meetsStatusSearch,
            };
          })
          // apply additional filters (and bypass if this group is to be bypassed)
          .filter(req => filters.showAllOfGroupId === gbk._id || req._meetsFilters)
          // sort within group: apply sort by subgroup first, then by date, then by start time
          .sort(
            (a, b) =>
              (a.subgroup?._id || '').localeCompare(b.subgroup?._id || '') ||
              a.date.getTime() - b.date.getTime() ||
              timeCompare(a.startTime, b.startTime)
          );

        // the amount of reqs that did not meet the additional filters
        const amtSkippedReqs = gbk.requests.length - relevantReqs.length;

        // use the metadata of what met filters to find the first 'real' request
        // this is important because this is how we sort the groups in the groups block
        // table. we cannot use the first request that exists (it could be from years ago).
        // we instead use the first request that meets the filters. thankfully we have this
        // info in the metadata calculated before the filters took place.
        const firstReq = relevantReqs.find(req => req._meetsFilters);

        return !firstReq
          ? undefined
          : {
              amtSkippedReqs,
              relevantReqs,
              firstDate: firstReq.date,
              _group: gbk,
            };
      })
      .filter(notUndefined)
      // groups are sorted within themselves, but not yet sorted against other groups
      .sort((a, b) => a.firstDate.getTime() - b.firstDate.getTime())
      .map(gbkBundle => {
        // add additional information (like sailing info) to the bookings
        const { relevantReqs, amtSkippedReqs, _group } = gbkBundle;
        const expandedReqIdx = relevantReqs.findIndex(bkg => bkg._id === expandedReqId);
        const color = hashStringToColor(_group.party.name, true);

        const rows = relevantReqs.map((req, i) => {
          const sailingDayInfo = (mixedDataBundle.sailingDayMap[isoDate(req.date)] || []).find(
            m => m.dayIndex === req.dayIndex
          );

          return {
            id: req._id,
            name: req.name,
            property: _group.property.name,
            venue: req.venue.name,
            voyage: sailingDayInfo?.sailing.name ?? '',
            location: sailingDayInfo?.day ? getDayLocation(sailingDayInfo.day) : '',
            day: req.dayIndex + 1,
            color,
            date: getLocaleUTCDateString(req.date),
            time: `<span class="whitespace-nowrap">${timeToString(
              req.startTime,
              hourFormat,
              DEFAULT_TIMESTR_FORMAT
            )} -</span>
              ${timeToString(
                calculateTimeOffset(req.startTime, req.duration).time,
                hourFormat,
                DEFAULT_TIMESTR_FORMAT
              )}`,
            requester: makeFullName(req.createdBy.firstName, req.createdBy.lastName),
            foodBeverage: req.resources.map(r => `${r.name} (amt. ${r.amount})`),
            entertainment: req.packages,
            housekeeping: req.dining || '',
            status: req.status,
            isFirstReq: !filters.groupByGroup ? false : i === 0,
            isLastReq: !filters.groupByGroup ? false : i === relevantReqs.length - 1,
            isExpandedReq: i === expandedReqIdx,
            totalNumRows: relevantReqs.length,
            hasExpandedReq: expandedReqIdx > -1,
            amtSkippedReqs,
            checked: selectedTableItems.findIndex(st => st.id === req._id) >= 0,
            _group,
            _sailingDay: sailingDayInfo?.day,
            _booking: req,
          };
        });

        return rows;
      })
      .flat();

    const bookings = bookingsSortedByGroups;
    setFilteredItems(bookings.filter(bk => bk._booking._meetsFilters));

    if (!filters.groupByGroup) {
      // kind of sucks, but now I have to sort again if the user does not want it grouped by group
      bookings.sort(
        (a, b) =>
          a._booking.date.getTime() - b._booking.date.getTime() ||
          timeToString(a._booking.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT).localeCompare(
            timeToString(b._booking.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT)
          )
      );
    }

    return bookings;
  }, [
    filters.selectedVoyages,
    filters.selectedLocations,
    filters.selectedStatuses,
    filters.selectedVenues,
    filters.groupId,
    filters.showAllOfGroupId,
    filters.groupByGroup,
    mixedDataBundle,
    fuseSearchResults,
    expandedReqId,
    selectedTableItems,
  ]);

  function makeDefaultTemplateTdProps(
    data: GroupsTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { isLastReq, isExpandedReq, color } = data;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isLastReq && !isExpandedReq ? 'border-b-2' : undefined,
        isExpandedReq ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
      style: {
        borderBottomColor: isLastReq ? color : '',
      },
    };
  }

  const tableFields: GroupsTableFields[] = [
    {
      key: 'group',
      _visible: filters.groupByGroup,
    },
    {
      key: 'groupBasic',
      tdProps: { className: 'p-1-i' },
      label: 'Group',
      _visible: !filters.groupByGroup,
    },
    {
      key: 'select',
      label: '',
      tdProps: { className: 'relative' },
      _visible: canBulkSelectBookings(user),
    },
    {
      key: 'property',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'p-1-i' },
      label: 'Property',
      _visible: filters.multiPropertyMode,
    },
    {
      key: 'subgroup',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'p-1-i' },
      label: 'Sub',
      _toggleable: true,
    },
    {
      key: 'name',
      _toggleable: true,
    },
    {
      key: 'voyage',
      _toggleable: true,
    },
    {
      key: 'location',
      _toggleable: true,
    },
    {
      key: 'day',
      _toggleable: true,
    },
    {
      key: 'venue',
      _toggleable: true,
    },
    {
      key: 'markers',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i' },
      _toggleable: true,
    },
    {
      key: 'date',
      _toggleable: true,
    },
    {
      key: 'time',
      _toggleable: true,
    },
    {
      key: 'requester',
      _toggleable: true,
    },
    {
      key: 'status',
      thProps: { className: 'pl-1-i' },
      tdProps: { className: 'p-1-i' },
      _toggleable: true,
    },
    {
      key: 'foodBeverage',
      label: 'Food & Beverage',
      _toggleable: true,
    },
    {
      key: 'entertainment',
      _toggleable: true,
    },
    {
      key: 'housekeeping',
      _toggleable: true,
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right' },
      _toggleable: true,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<GroupsTableItem>> = [
    {
      key: 'group',
      render: ({ isFirstReq, amtSkippedReqs, color, _group }) =>
        isFirstReq ? (
          <>
            <div className="mb-3">
              {_group.party.name.split(' ').map((v, i) => (
                <div key={i}>{v}</div>
              ))}
              <div>{_group.party.groupId}</div>
            </div>
            <div className="dropdown-inline-fix">
              <Dropdown
                items={menuDropdownItems.filter(md => {
                  if (md.key === 'booking-add' || md.key === 'edit-party') {
                    return !isMultiPropertyUnsafe(_group.property._id);
                  } else {
                    return true;
                  }
                })}
                onSelect={action => handleMenuDropdownAction(action, _group)}
              >
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    buttonRef={toggleRef}
                    {...rest}
                    variant="white"
                    className="shadow py-1-i"
                    style={{ color }}
                    icon="bars"
                  />
                )}
              </Dropdown>
            </div>
            {amtSkippedReqs > 0 && (
              <BubbleButton
                variant="white"
                inverse
                block
                className="mt-1 text-xxs text-white text-left font-bold px-1-i py-1-i whitespace-nowrap"
                onClick={() => setFilters({ ...filters, showAllOfGroupId: _group._id })}
              >
                <span>({amtSkippedReqs} reqs hidden)</span>
              </BubbleButton>
            )}
          </>
        ) : (
          <></>
        ),
      tdProps: ({ isFirstReq, totalNumRows, hasExpandedReq, color }) =>
        isFirstReq
          ? {
              rowSpan: totalNumRows + (hasExpandedReq ? 1 : 0),
              className: 'border-b-2 align-top text-md font-semibold text-white relative',
              width: '120px',
              style: {
                backgroundColor: color,
                borderColor: color,
              },
            }
          : { style: { display: 'none' } },
    },
    {
      key: 'groupBasic',
      tdProps: makeDefaultTemplateTdProps,
      render: ({ _group, color }) => (
        <div className="dropdown-inline-fix">
          <Dropdown
            items={menuDropdownItems}
            onSelect={action => handleMenuDropdownAction(action, _group)}
          >
            {({ toggleRef, ...rest }) => (
              <BubbleButton
                buttonRef={toggleRef}
                {...rest}
                variant="navy"
                inverse
                className="text-left"
                style={{ color }}
              >
                {_group.party.name}
              </BubbleButton>
            )}
          </Dropdown>
        </div>
      ),
    },
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          checked={item.checked}
          inputClassName="stretched-link bg-white"
          onValue={(checked: boolean) => {
            if (checked) {
              setSelectedTableItems([
                ...selectedTableItems.filter(ti => ti._group._id === item._group._id),
                item,
              ]);
            } else setSelectedTableItems(selectedTableItems.filter(st => st.id !== item.id));
          }}
        />
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'property',
      render: ({ property }) => (
        <BubbleBadge color={hashStringToColor(property)}>{property}</BubbleBadge>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'subgroup',
      render: ({ _group, _booking }) =>
        _booking.subgroup ? (
          <Tooltip text={_booking.subgroup.name || 'No name'}>
            <BubbleBadge
              className="cursor-pointer"
              clickable={canCreateOrEditBookings(user)}
              color={hashStringToColor(_booking.subgroup.label)}
              onClick={() =>
                canCreateOrEditBookings(user) &&
                _booking.subgroup &&
                setMode({
                  type: 'subgroup-edit',
                  groupId: _group._id,
                  subgroupId: _booking.subgroup._id,
                })
              }
            >
              {_booking.subgroup?.label}
            </BubbleBadge>
          </Tooltip>
        ) : (
          <BubbleBadge variant="smoke">N/A</BubbleBadge>
        ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'name',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'voyage',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'location',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'day',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'venue',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'date',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'time',
      render: data => <div dangerouslySetInnerHTML={{ __html: data.time }}></div>,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'requester',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'status',
      render: data => makeBadgeFromStatus(data.status),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'foodBeverage',
      render: data => (
        <div className="flex flex-wrap">
          {data.foodBeverage.map((fb, i) => (
            <div key={i} className="whitespace-nowrap">
              {fb}
            </div>
          ))}
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'entertainment',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'housekeeping',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'markers',
      render: data => <DayMarkersButton sailingDay={data._sailingDay} />,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'actions',
      render: data => (
        <>
          <BubbleButton
            onClick={() =>
              setExpandedReqId(expandedReqId === data._booking._id ? undefined : data._booking._id)
            }
            className="p-2-i"
            variant="navy"
            inverse
            icon={
              expandedReqId === data._booking._id
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
          />
          {canCreateOrEditBookings(user) && (
            <>
              <BubbleButton
                onClick={() => handleEditBooking(data._group, data._booking)}
                className="p-2-i"
                variant="navy"
                inverse
                disabled={isMultiPropertyUnsafe(data._group.property._id)}
                icon="edit"
              />
              <BubbleButton
                onClick={() => handleCloneBooking(data._group, data._booking)}
                className="p-2-i"
                variant="slate"
                inverse
                disabled={isMultiPropertyUnsafe(data._group.property._id)}
                icon="clone"
              />
            </>
          )}
        </>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
  ];

  const menuDropdownItems = [
    canCreateOrEditBookings(user)
      ? { key: 'booking-add', element: 'Add Group Booking' }
      : undefined,
    canCreateOrEditBookings(user) ? { key: 'edit-party', element: 'Edit Group' } : undefined,
    { key: 'memo-csv', element: 'Export Memo' },
  ].filter(notUndefined);

  function isMultiPropertyUnsafe(propertyId: string) {
    return filters.multiPropertyMode && activeProperty._id != propertyId;
  }

  async function handleMenuDropdownAction(action: DropdownItem, group: GroupBooking) {
    switch (action.key) {
      case 'booking-add':
        if (isMultiPropertyUnsafe(group.property._id)) return;
        handleAddBookingToGroup(group);
        break;
      case 'edit-party':
        if (isMultiPropertyUnsafe(group.property._id)) return;
        setMode({ type: 'group-edit', groupId: group._id });
        break;
      case 'memo-csv':
        let opts: {
          approvedOnly: boolean;
          startTime?: string;
          endTime?: string;
        } = { approvedOnly: false };

        const shouldContinue = await Alert.question({
          htmlContent: (
            <div className="p-3 w-full">
              <div className="mb-1 text-navy uppercase font-semibold text-xs">
                Start Date (Optional)
              </div>
              <BubbleInput
                type="date"
                className="mb-3"
                block
                value={opts.startTime}
                onValue={v => (opts.startTime = v)}
              />
              <div className="mb-1 text-navy uppercase font-semibold text-xs">
                End Date (Optional)
              </div>
              <BubbleInput
                type="date"
                block
                className="mb-3"
                value={opts.endTime}
                onValue={v => (opts.endTime = v)}
              />
              <div className="flex relative">
                <input
                  type="checkbox"
                  className="bubble-input"
                  onChange={evt => (opts.approvedOnly = evt.target.checked)}
                />
                <span className="ml-1">Approved Only</span>
              </div>
            </div>
          ),
          title: 'Select Date Range',
          yesButton: 'Export Memo',
          noButton: 'Cancel',
        });

        if (!shouldContinue) return;

        const result = await exportGroupMemo(
          group._id,
          opts.approvedOnly,
          opts.startTime,
          opts.endTime
        );

        return Alert.success({
          htmlContent: (
            <div className="pt-2 pb-4">
              <Anchor target="_blank" href={result}>
                Download PDF
              </Anchor>
            </div>
          ),
        });
    }
  }

  const statusDropdownItems = [
    { key: 'bulk-approve', element: 'Approved' },
    { key: 'bulk-reject', element: 'Rejected' },
    { key: 'bulk-draft', element: 'Draft' },
    { key: 'bulk-modified', element: 'Modified' },
    { key: 'bulk-pending', element: 'Pending' },
    { key: 'bulk-cancel', element: 'Cancelled' },
  ];

  async function handleStatusDropdownAction(action: DropdownItem) {
    switch (action.key) {
      case 'bulk-approve':
        await handleBulkStatusChangeBookings('approved');
        break;
      case 'bulk-reject':
        await handleBulkStatusChangeBookings('rejected');
        break;
      case 'bulk-draft':
        await handleBulkStatusChangeBookings('draft');
        break;
      case 'bulk-modified':
        await handleBulkStatusChangeBookings('modified');
        break;
      case 'bulk-pending':
        await handleBulkStatusChangeBookings('pending');
        break;
      case 'bulk-cancel':
        await handleBulkStatusChangeBookings('cancelled');
        break;
    }
  }

  function tableDetailsRender(item: GroupsTableItem) {
    return (
      <GroupsGlance
        user={user}
        property={item._group.property}
        groupId={item._group._id}
        bookingId={item._booking._id}
        data={{
          group: bare(item._group),
          booking: item._booking,
          sailingBundle: findSailingBundleByDateAndDay(
            item._booking.date,
            item._booking.dayIndex,
            mixedDataBundle.simpleSailings
          ),
        }}
        onFetchData={fetchData}
      />
    );
  }

  function handleCloseGroupBookingForm() {
    setMode({ type: 'default' });
  }

  function handleAddBookingToGroup(group: GroupBooking) {
    setMode({ type: 'create', groupId: group._id, startAtPage: 1 });
  }

  function handleEditBooking(group: GroupBooking, booking: VenueRequest) {
    setMode({ type: 'edit', groupId: group._id, bookingId: booking._id });
  }

  function handleCloneBooking(group: GroupBooking, booking: VenueRequest) {
    setMode({ type: 'create', groupId: group._id, bookingId: booking._id, startAtPage: 0 });
  }

  async function handleExportGroupBookings() {
    setIsBusy(prev => ({
      ...prev,
      exportBooking: true,
    }));
    try {
      await exportGroupBookings(activeProperty._id);
    } finally {
      setIsBusy(prev => ({
        ...prev,
        exportBooking: false,
      }));
    }
  }

  async function handleExportReport() {
    setIsBusy(prev => ({ ...prev, exportReport: true }));

    const { startDate, endDate } = filters;
    const requests = filteredItems
      ?.filter(gbk => gbk._booking)
      .map(item => ({
        voyage: item.voyage,
        bookingId: item._group._id,
        requestId: item._booking._id,
      }));
    let approvedOnly = false;
    const shouldContinue = await Alert.question({
      htmlContent: (
        <div className="p-3 w-full">
          <div className="flex relative justify-center">
            <input
              type="checkbox"
              className="bubble-input"
              onChange={e => {
                approvedOnly = e.target.checked;
              }}
            />
            <span className="ml-1">Approved Only</span>
          </div>
        </div>
      ),
      title: 'Status',
      yesButton: 'Ok',
      noButton: 'Cancel',
    });

    if (!shouldContinue) {
      setIsBusy(prev => ({ ...prev, exportReport: false }));
      return;
    }

    const url = await safeFetch(
      groupBookingClient.generateReport({
        body: requests ?? [],
        query: { approvedOnly: approvedOnly.toString(), from: startDate || '', to: endDate || '' },
        params: { propertyId: activeProperty._id },
        headers: {
          authorization: `Bearer ${getUserToken()}`,
        },
      }),
      'string'
    );

    setIsBusy(prev => ({ ...prev, exportReport: false }));

    return Alert.success({
      htmlContent: (
        <div className="pt-2 pb-4">
          <Anchor target="_blank" href={url}>
            Download PDF
          </Anchor>
        </div>
      ),
    });
  }

  async function handleExportReportCSV() {
    setIsBusy(prev => ({ ...prev, exportReport: true }));

    const requests = filteredItems?.map(item => ({
      voyage: item.voyage,
      bookingId: item._group._id,
      requestId: item._booking._id,
    }));

    let approvedOnly = false;

    const shouldContinue = await Alert.question({
      htmlContent: (
        <div className="p-3 w-full">
          <div className="flex relative justify-center">
            <input
              type="checkbox"
              className="bubble-input"
              onChange={e => {
                approvedOnly = e.target.checked;
              }}
            />
            <span className="ml-1">Approved Only</span>
          </div>
        </div>
      ),
      title: 'Status',
      yesButton: 'Ok',
      noButton: 'Cancel',
    });

    if (!shouldContinue) {
      setIsBusy(prev => ({ ...prev, exportReport: false }));
      return;
    }

    try {
      await exportGroupReportCSV(activeProperty._id, requests ?? [], approvedOnly);
    } finally {
      setIsBusy(prev => ({ ...prev, exportReport: false }));
    }
  }

  async function handleExportGroupBookingsBrand() {
    setIsBusy(prev => ({
      ...prev,
      exportBooking: true,
    }));

    try {
      await exportGroupBookingsBrand();
    } finally {
      setIsBusy(prev => ({
        ...prev,
        exportBooking: false,
      }));
    }
  }

  async function handleCreateSubgroup() {
    if (!createSubgroupForm || selectedTableItems.length < 1) return;

    const label = `${createSubgroupForm.letter}${createSubgroupForm.number}`;
    if (label.length !== 2) return;

    const { _group } = selectedTableItems[0];
    const bareGroup = bare(_group);

    let existingSubgroup = bareGroup.subgroups.find(sg => sg.label === label);
    if (!existingSubgroup) {
      existingSubgroup = {
        _id: new ObjectId().toHexString(),
        label,
        name: '',
        notes: '',
        groupGuideName: '',
        groupGuideCabin: '',
        attendees: 0,
        files: [],
      };
      bareGroup.subgroups.push(existingSubgroup);
    }

    const relevantReqs = selectedTableItems.map(sti => sti._booking._id);
    bareGroup.requests.forEach(req => {
      if (relevantReqs.includes(req._id)) {
        req.subgroup = existingSubgroup?._id;
      }
    });

    await updateGroupBooking(bareGroup);
    await fetchData();

    setCreateSubgroupForm(undefined);
    setMode({ type: 'subgroup-edit', groupId: _group._id, subgroupId: existingSubgroup._id });
  }

  async function handleClearSubgroup() {
    if (!createSubgroupForm || selectedTableItems.length < 1) return;

    const { _group } = selectedTableItems[0];
    const bareGroup = bare(_group);

    const relevantReqs = selectedTableItems.map(sti => sti._booking._id);
    bareGroup.requests.forEach(req => {
      if (relevantReqs.includes(req._id)) {
        req.subgroup = undefined;
      }
    });

    await updateGroupBooking(bareGroup);
    await fetchData();

    setCreateSubgroupForm(undefined);
  }

  async function handleBulkStatusChangeBookings(status: VenueRequestStatus) {
    const itemsToProcess = selectedTableItems
      .filter(ti => ti._booking.status !== status)
      .filter(ti => canChangeBookingStatus(status, user, ti._booking.venue._id));

    const shouldContinue = await Alert.question({
      message: `This action will change the status of the booking(s) to ${status.toLocaleUpperCase()}, provided that you have the necessary permissions to do so. Are you certain that you want to proceed with this change?`,
      title: `Change Group Booking Status`,
      yesButton: `Yes, change group booking status`,
      noButton: 'No, cancel',
      type: 'yesno',
    });

    if (!shouldContinue) return;

    await bulkBookingStatusChange(
      itemsToProcess.map(itp => ({
        group: bare(itp._group),
        booking: bareVenueRequest(itp._booking),
      })),
      user,
      status
    );

    await fetchData();
  }

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : filters.visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  const filterPresetOpts = [
    { key: 'default', element: 'Default Filters' },
    { key: 'foodBeverage', element: 'Food & Beverage Focus' },
    { key: 'entertainment', element: 'Entertainment Focus' },
    { key: 'housekeeping', element: 'Housekeeping Focus' },
  ];

  function handleChangeFilterPresetOpts(item: DropdownItem) {
    switch (item.key) {
      case 'default':
        setFilters({ ...filters, ...DEFAULT_FILTERS });
        break;
      case 'foodBeverage':
        setFilters({ ...filters, ...FOOD_BEVERAGE_FILTERS });
        break;
      case 'entertainment':
        setFilters({ ...filters, ...ENTERTAINMENT_FILTERS });
        break;
      case 'housekeeping':
        setFilters({ ...filters, ...HOUSEKEEPING_FILTERS });
        break;
    }
  }

  return (
    <PageWrapper {...props}>
      <Page className="Groups absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-forest-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-3 bg-forest-darker`}>
            <div className="grid gap-2">
              {canCreateOrEditBookings(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  block
                  disabled={loading}
                  onClick={() => setMode({ type: 'create', startAtPage: 0 })}
                  style={{ fontSize: '12px' }}
                  icon="sparkles"
                >
                  New Group Booking
                </BubbleButton>
              )}

              <div className="w-full flex items-stretch">
                <BubbleOverlay
                  loading={isBusy.exportBooking}
                  loadingClassName="rounded"
                  className="flex-grow"
                >
                  <BubbleButton
                    variant="slate"
                    className="text-left"
                    block
                    disabled={isBusy.exportBooking}
                    onClick={handleExportGroupBookings}
                    icon="file-export"
                    style={
                      canExportGroupBookingAllProperties(user)
                        ? {
                            paddingRight: 0,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            fontSize: '12px',
                          }
                        : undefined
                    }
                  >
                    Export Group Bookings
                  </BubbleButton>
                </BubbleOverlay>
                {canExportGroupBookingAllProperties(user) && (
                  <div className="dropdown-inline-flex-fix items-stretch">
                    <Dropdown
                      items={[{ key: 'brand-export', element: 'Export all properties' }]}
                      className="flex items-stretch"
                      onSelect={handleExportGroupBookingsBrand}
                    >
                      {({ toggleRef, ...rest }) => (
                        <BubbleButton
                          buttonRef={toggleRef}
                          {...rest}
                          variant="slate"
                          className="shadow py-1-i px-2-i"
                          icon="chevron-down"
                          disabled={isBusy.exportBooking}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                          }}
                        />
                      )}
                    </Dropdown>
                  </div>
                )}
              </div>
              <div className="w-full flex items-stretch">
                <BubbleOverlay
                  loading={isBusy.exportReport}
                  loadingClassName="rounded"
                  className="flex-grow"
                >
                  <BubbleButton
                    variant="slate"
                    className="text-left"
                    block
                    disabled={loading}
                    onClick={handleExportReport}
                    icon="file-export"
                    style={
                      canViewGroupsMultiPropertyCheckbox(serverInfo)
                        ? {
                            paddingRight: 0,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            fontSize: '12px',
                          }
                        : undefined
                    }
                  >
                    Export Report
                  </BubbleButton>
                </BubbleOverlay>
                <div className="dropdown-inline-flex-fix items-stretch">
                  <Dropdown
                    items={[{ key: 'brand-export', element: 'Export as CSV' }]}
                    className="flex items-stretch"
                    onSelect={handleExportReportCSV}
                  >
                    {({ toggleRef, ...rest }) => (
                      <BubbleButton
                        buttonRef={toggleRef}
                        {...rest}
                        variant="slate"
                        className="shadow py-1-i px-2-i"
                        icon="chevron-down"
                        disabled={loading}
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        }}
                      />
                    )}
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
          <BubbleAccordion
            variant="forest"
            className="flex-grow flex flex-col border-t border-forest-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4">
              <div className="dropdown-inline-fix sticky top-0 z-10 bg-forest-darkest p-4 -mt-4">
                <Dropdown items={filterPresetOpts} onSelect={handleChangeFilterPresetOpts}>
                  {({ toggleRef, ...rest }) => (
                    <BubbleButton
                      buttonRef={toggleRef}
                      {...rest}
                      variant="banana"
                      className="text-left"
                      block
                      icon="bolt-lightning"
                    >
                      Filter Presets
                    </BubbleButton>
                  )}
                </Dropdown>
              </div>
              {canViewGroupsMultiPropertyCheckbox(serverInfo) && (
                <div className="grid gap-1 text-white-i px-4">
                  <div className="text-white flex relative">
                    <BubbleInput
                      type="checkbox"
                      variant="forest"
                      checked={filters.multiPropertyMode}
                      inputClassName="stretched-link"
                      className="mr-1"
                      onValue={(checked: boolean) => {
                        setFilters({ ...filters, multiPropertyMode: checked });
                      }}
                    />
                    <span>Multi property</span>
                  </div>
                  {filters.multiPropertyMode && (
                    <BubbleTypeahead
                      block
                      variant="forest"
                      placeholder="Filter properties shown..."
                      menuClassName="max-h-80 overflow-y-auto"
                      value={filters.selectedProperties}
                      labelExtractor={p => p.name}
                      options={propertyOpts}
                      onAdd={v =>
                        setFilters({
                          ...filters,
                          selectedProperties: [...filters.selectedProperties, v],
                        })
                      }
                      onRemove={v =>
                        setFilters({
                          ...filters,
                          selectedProperties: filters.selectedProperties.filter(
                            v2 => v2.key !== v.key
                          ),
                        })
                      }
                      onClearAll={() => setFilters({ ...filters, selectedProperties: [] })}
                    />
                  )}
                </div>
              )}
              <BubbleFormGroup label="Dates" labelClassName="text-white-i" className="px-4">
                <div className="grid grid-cols-1 gap-1">
                  <BubbleDebounce
                    onTrigger={[
                      (date: string) => {
                        let newStartDate = date;
                        let newEndDate = filters.endDate;
                        let halfYearAfterStartDate = addDaysToUTCDate(new Date(newStartDate), 180);

                        // when changing the start date, we 'clamp' the end date if its too far
                        // from the start date to a maximum of half a year (180 days)
                        if (
                          !newEndDate ||
                          newStartDate > newEndDate ||
                          new Date(newEndDate) > halfYearAfterStartDate
                        ) {
                          newEndDate = isoDate(halfYearAfterStartDate);
                        }

                        setFilters({
                          ...filters,
                          startDate: newStartDate,
                          endDate: newEndDate,
                        });
                      },
                      500,
                    ]}
                  >
                    {({ debounce }) => (
                      <BubbleInput
                        variant="forest"
                        type="date"
                        value={filters.startDate}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                  <BubbleDebounce
                    onTrigger={[(date: string) => setFilters({ ...filters, endDate: date }), 500]}
                  >
                    {({ debounce }) => (
                      <BubbleInput
                        variant="forest"
                        type="date"
                        value={filters.endDate}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                </div>
              </BubbleFormGroup>
              <BubbleFormGroup label="Group" labelClassName="text-white-i" className="px-4">
                <BubbleInput
                  block
                  variant="forest"
                  placeholder="Filter by group ID..."
                  type="text"
                  value={filters.groupId}
                  onValue={val =>
                    setFilters({
                      ...filters,
                      groupId: val,
                    })
                  }
                />
                <div className="text-white flex relative mt-1">
                  <BubbleInput
                    type="checkbox"
                    variant="forest"
                    checked={filters.groupByGroup}
                    inputClassName="stretched-link"
                    className="mr-1"
                    onValue={(checked: boolean) => {
                      setFilters({ ...filters, groupByGroup: checked });
                    }}
                  />
                  <span>Group rows by group</span>
                </div>
              </BubbleFormGroup>
              <BubbleFormGroup label="Voyages" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter voyages shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedVoyages}
                  options={voyageOpts}
                  onAdd={v =>
                    setFilters({ ...filters, selectedVoyages: [...filters.selectedVoyages, v] })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedVoyages: filters.selectedVoyages.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedVoyages: [] })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Locations" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter locations shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedLocations}
                  options={locationOpts}
                  onAdd={l =>
                    setFilters({ ...filters, selectedLocations: [...filters.selectedLocations, l] })
                  }
                  onRemove={l =>
                    setFilters({
                      ...filters,
                      selectedLocations: filters.selectedLocations.filter(sl => sl.key !== l.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedLocations: [] })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Venues" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter venues shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  labelExtractor={opt => opt.name}
                  value={filters.selectedVenues}
                  options={venueOpts}
                  onAdd={v =>
                    setFilters({ ...filters, selectedVenues: [...filters.selectedVenues, v] })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedVenues: filters.selectedVenues.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedVenues: [] })}
                />
              </BubbleFormGroup>

              <BubbleFormGroup label="Status" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter statuses shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedStatuses}
                  options={statusOpts}
                  onAdd={v =>
                    setFilters({ ...filters, selectedStatuses: [...filters.selectedStatuses, v] })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedStatuses: filters.selectedStatuses.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedStatuses: [] })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Fields" labelClassName="text-white-i" className="px-4 pb-4">
                <div className="grid grid-cols-1">
                  {toggleableTableFields.map(tf => (
                    <div key={tf.key} className="text-white flex relative py-1">
                      <BubbleInput
                        type="checkbox"
                        variant="forest"
                        className="mr-1"
                        inputClassName="stretched-link"
                        checked={filters.visibleFields[tf.key]}
                        onValue={chk =>
                          setFilters({
                            ...filters,
                            visibleFields: { ...filters.visibleFields, [tf.key]: chk },
                          })
                        }
                      />
                      <span>{tf.label || camelToSentenceCase(tf.key)}</span>
                    </div>
                  ))}
                </div>
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 bg-blue-lightest"
          >
            <div className="mb-3 w-full flex items-end px-3">
              <GroupsNavigation active="bookings"></GroupsNavigation>
            </div>
          </div>
          <div className="GroupsBody mx-5 content-minus-logo min-w-900">
            <BubbleOverlay loading={loading}>
              <BubbleTable
                responsive
                striped
                headVariant="forest"
                fields={visibleTableFields}
                items={tableItems}
                onRowDblClick={item => {
                  canCreateOrEditBookings(user) &&
                    !isMultiPropertyUnsafe(item._group.property._id) &&
                    handleEditBooking(item._group, item._booking);
                }}
                keyExtractor={item => item.id}
                stickyHeader={130}
                templates={tableTemplates}
                detailsKey={expandedReqId}
                detailsRender={tableDetailsRender}
                detailsClassName="bg-bubblegum-lightest"
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div className="px-4 pt-4 space-x-3 pb-1 flex items-center">
                    {selectedTableItems.length > 0 && (
                      <>
                        {selectedTableItems
                          .map(sti => canApproveOrRejectABooking(user, sti._booking.venue._id))
                          .findIndex(v => !v) < 0 && (
                          <div className="dropdown-inline-fix">
                            <Dropdown
                              items={statusDropdownItems}
                              onSelect={action => handleStatusDropdownAction(action)}
                            >
                              {({ toggleRef, ...rest }) => (
                                <BubbleButton buttonRef={toggleRef} {...rest} variant="lime">
                                  Change Status
                                </BubbleButton>
                              )}
                            </Dropdown>
                          </div>
                        )}
                        {canCreateOrEditBookings(user) && (
                          <BubbleButton
                            variant="grape"
                            onClick={() =>
                              setCreateSubgroupForm(
                                generateDefaultSubgroupForm(selectedTableItems[0]._group.subgroups)
                              )
                            }
                            disabled={isMultiPropertyUnsafe(
                              selectedTableItems[0]._group.property._id
                            )}
                          >
                            Apply Subgroup
                          </BubbleButton>
                        )}
                        <BubbleButton
                          variant="white"
                          inverse
                          className="m-0-i"
                          onClick={() => setSelectedTableItems([])}
                        >
                          Clear
                        </BubbleButton>
                      </>
                    )}
                    <div className="flex-grow m-0-i">
                      <BubbleInput
                        variant="forest"
                        inputClassName="outline-none-i"
                        type="text"
                        block
                        value={search}
                        onValue={setSearch}
                        placeholder="Search by group name or group booking..."
                      />
                    </div>
                  </div>
                )}
              />
            </BubbleOverlay>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode.type !== 'default' && (
          <BubbleDrawer hideTools containerClassName="bg-eggshell">
            <GroupBookingForm
              mode={mode}
              data={dataBundle}
              property={activeProperty}
              user={user}
              groupTypes={serverInfo.brand.groupTypes}
              onClose={handleCloseGroupBookingForm}
              defaultStartDate={filters.startDate}
              defaultEndDate={filters.endDate}
              onSubmitGroupBookings={fetchData}
            />
          </BubbleDrawer>
        )}
        {createSubgroupForm && (
          <Popup title="Apply Subgroup" onClose={() => setCreateSubgroupForm(undefined)}>
            <div style={{ width: '600px' }}>
              <hr />
              <div className="p-4">
                {createSubgroupForm._advanced && (
                  <div className="mb-5">
                    <BubbleFormGroup label="Letter" asterisk className="mb-5">
                      <div className="grid gap-3 grid-cols-10">
                        {Array.from(Array(26))
                          .map((_, i) => i + 65)
                          .map(x => (
                            <BubbleButton
                              variant={
                                createSubgroupForm.letter === String.fromCharCode(x)
                                  ? 'lime'
                                  : 'smoke'
                              }
                              key={x}
                              onClick={() =>
                                setCreateSubgroupForm({
                                  ...createSubgroupForm,
                                  letter: String.fromCharCode(x),
                                })
                              }
                            >
                              {String.fromCharCode(x)}
                            </BubbleButton>
                          ))}
                      </div>
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Number" asterisk>
                      <div className="grid gap-3 grid-cols-10">
                        {Array.from(Array(10).keys()).map(x => (
                          <BubbleButton
                            variant={createSubgroupForm.number === x.toString() ? 'lime' : 'smoke'}
                            key={x}
                            onClick={() =>
                              setCreateSubgroupForm({
                                ...createSubgroupForm,
                                number: x.toString(),
                              })
                            }
                          >
                            {x}
                          </BubbleButton>
                        ))}
                      </div>
                    </BubbleFormGroup>
                  </div>
                )}
                {createSubgroupForm.letter.length > 0 && createSubgroupForm.number.length > 0 && (
                  <div className="flex items-center">
                    <span className="italic mr-2 text-slate">Will show as:</span>
                    <BubbleBadge
                      variant="smoke"
                      color={hashStringToColor(
                        `${createSubgroupForm.letter}${createSubgroupForm.number}`
                      )}
                    >
                      {createSubgroupForm.letter}
                      {createSubgroupForm.number}
                    </BubbleBadge>
                    {!createSubgroupForm._advanced && (
                      <BubbleButton
                        variant="navy"
                        inverse
                        className="p-1-i ml-auto"
                        onClick={() =>
                          setCreateSubgroupForm({ ...createSubgroupForm, _advanced: true })
                        }
                      >
                        OR, specify a custom label
                      </BubbleButton>
                    )}
                  </div>
                )}
              </div>
              <hr />
              <div className="p-4">
                <div className="flex items-center">
                  <BubbleButton
                    variant="navy"
                    className="mr-3"
                    disabled={
                      createSubgroupForm.letter.length < 1 || createSubgroupForm.number.length < 1
                    }
                    onClick={handleCreateSubgroup}
                  >
                    Apply Subgroup
                  </BubbleButton>
                  <BubbleButton variant="crimson" onClick={handleClearSubgroup}>
                    Remove (Mark as N/A)
                  </BubbleButton>
                </div>
              </div>
            </div>
          </Popup>
        )}
      </Page>
    </PageWrapper>
  );
};

export default GroupsHome;

export function makeVariantFromStatus(status: VenueRequestStatus) {
  let variant: BubbleBadgeProps['variant'] = 'smoke';

  switch (status) {
    case 'approved':
      variant = 'lime';
      break;
    case 'cancelled':
    case 'rejected':
      variant = 'crimson';
      break;
    case 'draft':
    case 'modified':
      variant = 'banana';
      break;
    default:
      variant = 'smoke';
      break;
  }

  return variant;
}

export function makeBadgeFromStatus(status: VenueRequestStatus) {
  const variant = makeVariantFromStatus(status);

  return <BubbleBadge variant={variant}>{status}</BubbleBadge>;
}

export function makeTextFromStatus(status: VenueRequestStatus) {
  const variant = makeVariantFromStatus(status);

  return <span className={`text-xs uppercase font-bold text-${variant}`}>{status}</span>;
}
